import { space, tok } from '@sunwebgroup/ui-components'
import { ToggleButton, ToggleButtonGroup, Typography, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { format } from 'date-fns'
import * as React from 'react'
import { use, useState } from 'react'
import { styled } from 'styled-components'
import { capitalize } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useGridTabsContext } from '@/scopes/prices/components/GridTabsContext'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { getPriceRangeSelector } from '@/scopes/prices/components/selectors'
import { createPricesFilterClickEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const ToggleGroup = styled(ToggleButtonGroup)`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_24')};
`

const YearWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_6')};
`

const MonthsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_8')};
`

const MonthButton = styled(ToggleButton)`
  background: ${tok('semantics_views_background_color')};
  min-width: 76px;
`

const NextSeasonButton = styled(ToggleButton)`
  background: ${tok('semantics_views_background_color')};
  min-width: 76px;
`

export function MonthFilter() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()
  const { sendEvent } = useAnalytics()
  const { selectFilterMonth } = use(PricesActionsContext)
  const { options } = usePricesSelector(getPriceRangeSelector)
  const { focusedDate, navigationDateTo, navigateToMonth } = use(NavigationByWeeksContext)
  const { selectedTab, setSelectedTab } = useGridTabsContext()
  const [focusedMonth, setFocusedMonth] = useState('')

  const handleOnClick = (value: string) => {
    if (!value) {
      // when clicking on the active month, value is empty
      return
    }
    setSelectedTab(value)

    if (value === 'alertService')
      return

    selectFilterMonth(value, navigateToMonth)

    sendEvent(
      createPricesFilterClickEvent({
        element: 'tab',
        type: 'month',
        result: value,
      }),
    )
  }

  const selectedMonth = React.useMemo(() => {
    if (!navigationDateTo || (!isTabletOrGreater && !focusedDate))
      return

    if (selectedTab === 'alertService')
      return 'alertService'

    const date = isTabletOrGreater ? navigationDateTo : focusedDate!
    const fromMonth = format(date, 'yyyy-MM')
    const selectedMonth = Object.values(options)
      .flat()
      .find(({ value }) => value === fromMonth)
      ?.value

    if (selectedMonth && selectedMonth !== focusedMonth) {
      setFocusedMonth(selectedMonth)
      setSelectedTab(selectedMonth)
    }

    return selectedMonth
  }, [navigationDateTo, options, selectedTab, isTabletOrGreater, focusedDate])

  const handleNextSeasonClick = () => {
    setSelectedTab('alertService')
  }

  return (
    <>
      <ToggleGroup type="single" onValueChange={handleOnClick} value={selectedMonth}>
        {Object.keys(options).map((key: string, index) => (
          <YearWrapper key={key}>
            <Typography variant="display-g-regular">{key}</Typography>
            <MonthsWrapper>
              {options[key].map(({ value }) => (
                <MonthButton
                  id={value}
                  key={value}
                  value={value}
                  title={capitalize(new Date(value).toLocaleString(intl.locale, { month: 'short' }))}
                  size="S"
                />
              ))}
              {index === Object.keys(options).length - 1 && <NextSeasonButton onClick={handleNextSeasonClick} value="alertService" title={intl.formatMessage({ id: 'price_alert_service-next-season' })} size="S" />}
            </MonthsWrapper>
          </YearWrapper>
        ))}
      </ToggleGroup>
    </>
  )
}
